import {monthlyBonusViews, salaryCompositionViews} from './deal-payroll-detail/deal-payroll-detail.view';
import {payrollNumberDisplay} from '../../models/payroll-number-display';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyCode } from '../../models/payroll-currency.model';
import {
  DEAL_PAYROLL_STATUS,
  DealPayrollBreakdown,
  isFinitePayrollNumber,
  isPlanfixDealPayroll
} from '../../models/deal-payroll.model';
import { hasTrustedDealPayrollModifiers } from '../../models/deal-payroll-report-presentation';
import {
  ACTUAL_TIME_WORKED_KEY,
  ALL_TIME_KEY,
  CALCULATED_TASK_AMOUNT_KEY,
  CALCULATION_BASE_PAY_KEY,
  CALCULATION_HOURS_KEY,
  CURRENCY_CONVERSION_NOTE_KEY,
  PAYROLL_MODIFIERS,
  EMPTY_TEXT,
  EMPTY_TOOLTIP_CONTENT,
  FINAL_SALARY_KEY,
  GUARANTEED_BASE_KEY,
  HOUR_KEY,
  MODIFIERS_FROM_REPORT_KEY,
  MODIFIERS_UNAVAILABLE_KEY,
  PAYABLE_TASK_AMOUNT_KEY,
  PAYROLL_CALCULATION_INDEX,
  PRE_MODIFIER_BASE_KEY,
  PREVIEW_FINAL_SALARY_KEY,
  TOOLTIP_ACTUAL_SEPARATOR,
  TOOLTIP_ADD_PREFIX,
  TOOLTIP_COLORED_SPAN_MIDDLE,
  TOOLTIP_COLORED_SPAN_START,
  TOOLTIP_DOUBLE_LINE_BREAK,
  TOOLTIP_DIVIDE_PREFIX,
  TOOLTIP_EMPTY_AMOUNT,
  TOOLTIP_EQUALS_PREFIX,
  TOOLTIP_LABEL_SEPARATOR,
  TOOLTIP_LINE_BREAK,
  TOOLTIP_MULTIPLY_PREFIX,
  TOOLTIP_RED_SPAN_START,
  TOOLTIP_SPACE,
  TOOLTIP_SPAN_END,
  TOOLTIP_TYPES,
  TOOLTIP_COLORS,
  TooltipType,
  TOTAL_AMOUNT_KEY,
  TOTAL_WORKING_HOURS_KEY,
  USER_PAY_RATE_KEY
} from './my-working-report.constants';
import { ReportViewModel } from './my-working-report.types';

@Injectable()
export class MyWorkingReportTooltipBuilder {
  constructor(
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {
  }

  public content(report: ReportViewModel, type: TooltipType): SafeHtml {
    const calculation = report.howSalaryCalculated;
    const currency = report.summaryRecord.displayCurrency;
    let content = EMPTY_TOOLTIP_CONTENT;
    if (type === TOOLTIP_TYPES.WAGE_RATE) {
      content = isPlanfixDealPayroll(report) ? this.dealTooltip(calculation, report) : this.wageTooltip(calculation, currency);
    } else if (type === TOOLTIP_TYPES.SALARY_SUPPLEMENTS) {
      content = isPlanfixDealPayroll(report)
        ? this.dealTooltip(calculation, report)
        : this.supplementsTooltip(calculation, report.summaryRecord.salarySupplements, currency);
    }
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }

  public canShow(report: ReportViewModel, type: TooltipType): boolean {
    if (!isPlanfixDealPayroll(report)) {
      return true;
    }
    if (type === TOOLTIP_TYPES.WAGE_RATE) {
      return !!report.howSalaryCalculated
        && report.howSalaryCalculated.length > PAYROLL_CALCULATION_INDEX.CALCULATED_WAGE;
    }
    return type === TOOLTIP_TYPES.SALARY_SUPPLEMENTS && !!report.dealPayrollBreakdown
      && report.dealPayrollBreakdown.status !== DEAL_PAYROLL_STATUS.DISABLED;
  }

  private wageTooltip(
    calculation: Array<string | number | null>,
    currency: CurrencyCode
  ): string {
    const index = PAYROLL_CALCULATION_INDEX;
    return this.salaryComposition(calculation, currency) + this.rateHeader(calculation, currency)
      + payrollNumberDisplay(calculation[index.BASE_RATE], true) + TOOLTIP_SPACE + this.translate.instant(USER_PAY_RATE_KEY) + TOOLTIP_LINE_BREAK
      + TOOLTIP_DIVIDE_PREFIX + this.value(calculation[index.PLANNED_HOURS]) + TOOLTIP_SPACE
      + this.translate.instant(TOTAL_WORKING_HOURS_KEY) + TOOLTIP_LINE_BREAK
      + TOOLTIP_MULTIPLY_PREFIX + this.value(calculation[index.ACTUAL_HOURS]) + TOOLTIP_SPACE
      + this.translate.instant(ACTUAL_TIME_WORKED_KEY) + TOOLTIP_LINE_BREAK
      + TOOLTIP_EQUALS_PREFIX + payrollNumberDisplay(calculation[index.CALCULATED_WAGE], true) + TOOLTIP_SPACE
      + this.translate.instant(TOTAL_AMOUNT_KEY);
  }

  private supplementsTooltip(
    calculation: Array<string | number | null>,
    total: number,
    currency: CurrencyCode
  ): string {
    const index = PAYROLL_CALCULATION_INDEX;
    const actual = Number(calculation[index.ACTUAL_HOURS]);
    const all = Number(calculation[index.ALL_HOURS]);
    const actualColor = calculation[index.ALL_HOURS] !== null ? this.color(actual, all) : EMPTY_TEXT;
    const allColor = calculation[index.ALL_HOURS] !== null ? this.color(all, actual) : EMPTY_TEXT;
    return this.salaryComposition(calculation, currency) + this.rateHeader(calculation, currency)
      + this.legacyHours(calculation, actualColor, allColor)
      + this.modifierLines(calculation)
      + TOOLTIP_EQUALS_PREFIX + payrollNumberDisplay(total, true) + TOOLTIP_SPACE + this.translate.instant(TOTAL_AMOUNT_KEY);
  }

  private rateHeader(
    calculation: Array<string | number | null>,
    currency: CurrencyCode
  ): string {
    const index = PAYROLL_CALCULATION_INDEX;
    return TOOLTIP_RED_SPAN_START + this.translate.instant(CALCULATION_BASE_PAY_KEY) + TOOLTIP_SPACE
      + payrollNumberDisplay(calculation[index.BASE_RATE], true) + TOOLTIP_SPACE + currency + TOOLTIP_SPACE
      + this.translate.instant(CALCULATION_HOURS_KEY) + TOOLTIP_SPACE
      + this.value(calculation[index.PLANNED_HOURS]) + TOOLTIP_SPACE + this.translate.instant(HOUR_KEY)
      + TOOLTIP_SPAN_END + TOOLTIP_DOUBLE_LINE_BREAK;
  }

  private legacyHours(
    calculation: Array<string | number | null>,
    actualColor: string,
    allColor: string
  ): string {
    const index = PAYROLL_CALCULATION_INDEX;
    let html = payrollNumberDisplay(calculation[index.BASE_RATE], true) + TOOLTIP_SPACE + this.translate.instant(USER_PAY_RATE_KEY)
      + TOOLTIP_LINE_BREAK + TOOLTIP_DIVIDE_PREFIX + this.value(calculation[index.PLANNED_HOURS]) + TOOLTIP_SPACE
      + this.translate.instant(TOTAL_WORKING_HOURS_KEY) + TOOLTIP_LINE_BREAK
      + TOOLTIP_MULTIPLY_PREFIX + this.coloredValue(
        calculation[index.ACTUAL_HOURS], actualColor, ACTUAL_TIME_WORKED_KEY);
    if (calculation[index.ALL_HOURS] !== null) {
      html += TOOLTIP_ACTUAL_SEPARATOR
        + this.coloredValue(calculation[index.ALL_HOURS], allColor, ALL_TIME_KEY);
    }
    return html + TOOLTIP_LINE_BREAK;
  }

  private coloredValue(value: string | number | null, color: string, labelKey: string): string {
    return TOOLTIP_COLORED_SPAN_START + color + TOOLTIP_COLORED_SPAN_MIDDLE
      + this.value(value) + TOOLTIP_SPACE + this.translate.instant(labelKey) + TOOLTIP_SPAN_END;
  }

  private dealTooltip(
    calculation: Array<string | number | null>,
    report: ReportViewModel
  ): string {
    const breakdown = report.dealPayrollBreakdown;
    if (!breakdown) {
      return EMPTY_TOOLTIP_CONTENT;
    }
    const sameCurrency = breakdown.currency === breakdown.salaryCurrency;
    let html = this.dealBase(breakdown, sameCurrency);
    if (!sameCurrency) {
      html += this.translate.instant(CURRENCY_CONVERSION_NOTE_KEY) + TOOLTIP_LINE_BREAK;
    }
    return monthlyBonusViews(calculation || [], breakdown.salaryCurrency)
      .map(row => this.line(row.key, String(row.value))).join(EMPTY_TOOLTIP_CONTENT)
      + html + TOOLTIP_LINE_BREAK + this.dealModifiers(calculation, report)
      + this.dealFinal(breakdown);
  }

  private dealBase(breakdown: DealPayrollBreakdown, sameCurrency: boolean): string {
    const taskLabel = breakdown.status === DEAL_PAYROLL_STATUS.COMPLETE
      ? PAYABLE_TASK_AMOUNT_KEY : CALCULATED_TASK_AMOUNT_KEY;
    return this.line(taskLabel, this.amount(this.taskAmount(breakdown), breakdown.currency))
      + (sameCurrency ? TOOLTIP_EQUALS_PREFIX : EMPTY_TOOLTIP_CONTENT)
      + this.line(PRE_MODIFIER_BASE_KEY, this.amount(breakdown.preModifierBase, breakdown.salaryCurrency));
  }

  private salaryComposition(calculation: Array<string | number | null>, currency: CurrencyCode): string {
    return salaryCompositionViews(calculation || [], currency)
      .map(row => this.line(row.key, String(row.value))).join(EMPTY_TOOLTIP_CONTENT);
  }

  private dealModifiers(
    calculation: Array<string | number | null>,
    report: ReportViewModel
  ): string {
    if (!calculation || calculation.length <= PAYROLL_CALCULATION_INDEX.LAST_MODIFIER) {
      return this.translate.instant(MODIFIERS_UNAVAILABLE_KEY) + TOOLTIP_LINE_BREAK;
    }
    let html = this.modifierLines(calculation);
    if (!hasTrustedDealPayrollModifiers(report)) {
      html += this.translate.instant(MODIFIERS_FROM_REPORT_KEY) + TOOLTIP_LINE_BREAK;
    }
    return html;
  }

  private modifierLines(calculation: Array<string | number | null>): string {
    return PAYROLL_MODIFIERS.map(modifier =>
      this.value(calculation[modifier.index])
      + TOOLTIP_SPACE + this.translate.instant(modifier.key) + TOOLTIP_LINE_BREAK
    ).join(EMPTY_TOOLTIP_CONTENT);
  }

  private dealFinal(breakdown: DealPayrollBreakdown): string {
    const key = breakdown.status === DEAL_PAYROLL_STATUS.COMPLETE
      ? FINAL_SALARY_KEY : PREVIEW_FINAL_SALARY_KEY;
    return this.line(key, this.amount(breakdown.finalSalary, breakdown.salaryCurrency), false);
  }

  private taskAmount(breakdown: DealPayrollBreakdown): number | null {
    return breakdown.status === DEAL_PAYROLL_STATUS.COMPLETE
      ? breakdown.payableTaskAmount : breakdown.calculatedTaskAmount;
  }

  private amount(value: number | null, currency: CurrencyCode): string {
    return isFinitePayrollNumber(value)
      ? payrollNumberDisplay(value, true) + TOOLTIP_SPACE + currency : TOOLTIP_EMPTY_AMOUNT;
  }

  private value(value: string | number | null): string {
    return payrollNumberDisplay(value);
  }

  private line(key: string, value: string, breakLine: boolean = true): string {
    return this.translate.instant(key) + TOOLTIP_LABEL_SEPARATOR + value
      + (breakLine ? TOOLTIP_LINE_BREAK : EMPTY_TOOLTIP_CONTENT);
  }

  private color(first: number, second: number): string {
    return first >= second ? TOOLTIP_COLORS.GREEN : TOOLTIP_COLORS.GREY;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/my-working-report-tooltip.builder.ts